import React, { useEffect } from "react"
import tw, { styled } from "twin.macro"
import { Description, Field, HelpText, Label } from "../input"

export enum MaxLength {
  Max160 = 160, // Profile -> Bio
  Max280 = 280, // All other uses...
}

export interface TextareaProps {
  name: string
  value: string
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  label?: string
  placeholder?: string
  maxLength?: number
  handleFocus?: () => void
  handleBlur?: () => void
  disabled?: boolean
  tabIndex?: number
  description?: string
  required?: boolean
  helpText?: string
  invalid?: boolean
  readOnly?: boolean
}
interface StyledProps {
  maxLength?: number
  name?: string
}

const StyledTextarea = styled("textarea")(({ maxLength, name }: StyledProps) => [
  tw`w-full px-2 py-2 pb-10 h-40`,
  tw`text-base-6 bg-input caret-purple`,
  tw`(border border-base-3 rounded)`,
  tw`placeholder:(text-base-3)`,
  tw`focus:(outline-none border-purple)`,
  tw`disabled:(text-base-2 border-base-2 placeholder:(text-base-2))`,
  tw`read-only:(focus:border-base-3)`,
  tw`invalid:(border-red)`,

  !!maxLength && maxLength == MaxLength.Max280 && tw`h-72 min-h-card`,
  !!maxLength && maxLength == MaxLength.Max280 && name == "question-body-input" && tw`h-24 min-h-0 tablet-lg:(h-96)`,
])

const Hint = styled("span")(() => [tw`absolute right-2 bottom-4 text-sm text-base-3`, tw`invalid:(text-red)`])

const Textarea = (props: TextareaProps) => {
  const {
    value,
    maxLength,
    onChange,
    name,
    placeholder,
    handleFocus,
    handleBlur,
    disabled,
    label,
    description,
    tabIndex,
    required,
    helpText,
    invalid,
    readOnly,
  } = props
  useEffect(() => {
    if (value.length > Number(maxLength)) {
      value.slice(0, Number(maxLength))
    }
  }, [value])

  return (
    <Field>
      <div>
        {label && (
          <Label data-testid={`${name}-label`} htmlFor={`${name}-id`}>
            {label}
          </Label>
        )}
        {description && (
          <Description id={`${name}-description`} data-testid={`${name}-description`}>
            {description}
          </Description>
        )}
      </div>

      {maxLength && (
        <span tw="text-sm text-base-4 sr-only" id={`${name}-hint`}>
          <span>Max limit of {maxLength} characters</span>
        </span>
      )}
      <div tw="relative space-y-1">
        <div tw="relative">
          <StyledTextarea
            placeholder={placeholder}
            aria-placeholder={placeholder}
            name={name}
            // aria-multiline="true"
            value={value}
            id={`${name}-id`}
            maxLength={maxLength}
            aria-describedby={`${name}-description ${name}-hint ${name}-info`}
            onChange={onChange}
            data-testid={`${name}-textarea`}
            onFocus={() => {
              handleFocus?.()
            }}
            onBlur={() => {
              handleBlur?.()
            }}
            disabled={disabled}
            tabIndex={tabIndex}
            required={!!required ? required : false}
            aria-required={!!required ? required : false}
            readOnly={readOnly}
            aria-readonly={readOnly}
          />
          {maxLength && (
            <Hint id={`${name}-info`} aria-live="polite">
              {`${value.length}/${maxLength}`}
            </Hint>
          )}
        </div>
        {helpText && (
          <HelpText data-testid={`${name}-help-text`} invalid={invalid}>
            {helpText}
          </HelpText>
        )}
      </div>
    </Field>
  )
}

export default Textarea
